import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
  LayoutDashboard, Package, Warehouse, Users, FileText, Send,
  RotateCcw, Truck, MapPinned, BarChart3, FileSpreadsheet, ScrollText,
  Search, LogOut, PlusCircle, History, Box, Home, Settings,
} from "lucide-react";

const NAV = {
  admin: [
    { to: "/", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/requests", icon: FileText, label: "Requests" },
    { to: "/stock", icon: Box, label: "Stock" },
    { to: "/serials", icon: Package, label: "Serials" },
    { to: "/products", icon: Package, label: "Products" },
    { to: "/warehouses", icon: Warehouse, label: "Warehouses" },
    { to: "/users", icon: Users, label: "Users" },
    { to: "/recoveries", icon: RotateCcw, label: "Recoveries" },
    { to: "/dispatches", icon: Truck, label: "Dispatches" },
    { to: "/projects", icon: MapPinned, label: "Projects" },
    { to: "/reports", icon: BarChart3, label: "Reports" },
    { to: "/excel", icon: FileSpreadsheet, label: "Excel Import" },
    { to: "/audit", icon: ScrollText, label: "Audit Log" },
    { to: "/settings", icon: Settings, label: "Settings" },
  ],
  cluster_head: [
    { to: "/", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/requests", icon: FileText, label: "Requests" },
    { to: "/stock", icon: Box, label: "Stock" },
    { to: "/serials", icon: Package, label: "Serials" },
    { to: "/users", icon: Users, label: "Engineers" },
    { to: "/recoveries", icon: RotateCcw, label: "Recoveries" },
    { to: "/projects", icon: MapPinned, label: "Projects" },
    { to: "/reports", icon: BarChart3, label: "Reports" },
  ],
  warehouse_manager: [
    { to: "/", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/requests", icon: FileText, label: "Requests" },
    { to: "/stock", icon: Box, label: "Stock" },
    { to: "/serials", icon: Package, label: "Serials" },
    { to: "/recoveries", icon: RotateCcw, label: "Recoveries" },
    { to: "/dispatches", icon: Truck, label: "Dispatches" },
  ],
  engineer: [
    { to: "/", icon: Home, label: "Home" },
    { to: "/requests", icon: FileText, label: "Requests" },
    { to: "/issues", icon: Send, label: "My Material" },
    { to: "/recoveries", icon: RotateCcw, label: "Recovery" },
  ],
};

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  if (!user) return null;
  const items = NAV[user.role] || [];

  const SidebarLink = ({ to, icon: Icon, label }) => (
    <NavLink
      to={to}
      end={to === "/"}
      data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
    >
      <span className="mark" />
      <Icon size={16} />
      <span>{label}</span>
    </NavLink>
  );

  const BottomLink = ({ to, icon: Icon, label }) => (
    <NavLink
      to={to}
      end={to === "/"}
      data-testid={`mnav-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={({ isActive }) => (isActive ? "active" : "")}
    >
      <Icon size={20} />
      <span>{label}</span>
    </NavLink>
  );

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex flex-col w-60 fixed left-0 top-0 h-screen bg-[#18181B] text-white border-r border-[#27272A] z-40">
        <div className="px-5 pt-6 pb-5 border-b border-[#27272A]">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-[#F97316] flex items-center justify-center font-display font-bold text-white">S</div>
            <div>
              <div className="font-display font-bold tracking-tight text-[15px] leading-none">Sanghvi</div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-400 mt-1">Inventory Portal</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
          {items.map((it) => <SidebarLink key={it.to} {...it} />)}
        </nav>
        <div className="border-t border-[#27272A] px-3 py-3">
          <div className="text-xs text-zinc-500 truncate">{user.email}</div>
          <div className="text-[10px] uppercase tracking-widest text-[#F97316] font-bold mt-1">{user.role.replace("_", " ")}</div>
          <button
            data-testid="logout-btn"
            onClick={() => { logout(); nav("/login"); }}
            className="mt-3 w-full inline-flex items-center justify-center gap-2 text-xs font-semibold bg-[#27272A] hover:bg-[#3F3F46] text-zinc-200 rounded-md py-2"
          >
            <LogOut size={13} /> Logout
          </button>
        </div>
      </aside>

      {/* Top bar mobile */}
      <header className="md:hidden fixed top-0 left-0 right-0 z-40 h-14 bg-[#18181B] text-white flex items-center justify-between px-4 border-b border-[#27272A]">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-[#F97316] flex items-center justify-center font-display font-bold text-white text-sm">S</div>
          <div className="font-display font-bold text-sm">Sanghvi Infotech</div>
        </div>
        <button
          data-testid="logout-btn-mobile"
          onClick={() => { logout(); nav("/login"); }}
          className="text-zinc-400 hover:text-white"
        >
          <LogOut size={18} />
        </button>
      </header>

      {/* Main content */}
      <main className="flex-1 md:ml-60 pt-14 md:pt-0 pb-20 md:pb-0 w-full">
        {children}
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden mob-nav">
        {items.slice(0, 5).map((it) => <BottomLink key={it.to} {...it} />)}
      </nav>
    </div>
  );
}
